import { Suspense } from 'react';
import {
  Play,
  CheckCircle2,
  AlertCircle,
  XCircle,
  Bot,
  PlayCircle,
  RefreshCw,
  ShieldCheck,
  GitFork,
  Cpu,
  Zap,
  Clock,
  Workflow,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import type { AxonEvent } from '@reflex/shared';
import { parseEventPayload } from '@reflex/ui/client/event-utils';
import { StreamCardBubble } from '@reflex/ui/components/stream/StreamCardBubble';
import { DividerBubble } from '@reflex/ui/components/stream/DividerBubble';
import { SectionLabel } from '@reflex/ui/components/layout/Typography';
import { MarkdownContent } from '@reflex/ui/components/content/MarkdownContent';
import { usePlugins } from '@reflex/ui/client/hooks';
import { useActiveOrgSlug } from '@reflex/ui/client/context';
import { ROUTES } from '@reflex/shared/routes';
import { cn } from '@reflex/ui/lib/utils';
import {
  parseJsonPayload,
  StructuredPayloadView,
} from '@reflex/ui/components/content/StructuredPayloadView';
import { useTriggerExternalRefs } from '../useExternalRefs';
import { buildExternalRefOverrides } from './RefChips';

function nodeTypeIcon(type: string) {
  switch (type) {
    case 'agent':
      return <Bot className="h-3.5 w-3.5" />;
    case 'agent_task':
      return <PlayCircle className="h-3.5 w-3.5" />;
    case 'approval':
      return <ShieldCheck className="h-3.5 w-3.5" />;
    case 'router':
      return <GitFork className="h-3.5 w-3.5" />;
    case 'llm':
      return <Cpu className="h-3.5 w-3.5" />;
    case 'action':
      return <Zap className="h-3.5 w-3.5" />;
    default:
      return null;
  }
}

interface FlowEventProps {
  event: AxonEvent;
}

/** Parse a stream `triggerType` label (`event:<source>/<event>`). */
function parseStreamEventTrigger(
  triggerType: string | undefined,
): { source: string; event: string } | undefined {
  const match = triggerType ? /^event:([^/]+)\/(.+)$/.exec(triggerType) : null;
  return match ? { source: match[1], event: match[2] } : undefined;
}

/** Renderer-registry lookup keys for a stream `triggerType` label. */
function streamTriggerKeys(triggerType: string): string[] {
  const eventRef = parseStreamEventTrigger(triggerType);
  if (eventRef) return [`${eventRef.source}:${eventRef.event}`, eventRef.event];
  return [triggerType];
}

export function FlowStartedBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    flowName?: string;
    triggerType?: string;
    triggerData?: Record<string, unknown> | null;
  }>(event.payload);

  const { flowTriggerRenderers } = usePlugins();
  const externalRefs = useTriggerExternalRefs(
    parseStreamEventTrigger(payload?.triggerType),
    payload?.triggerData ?? null,
  );
  // The stream's `triggerType` is `event:<source>/<event>` for event
  // triggers (plus `cron`/`manual`, or a raw legacy type). Renderer
  // registrations are keyed by event name, so try the parsed keys first.
  const rendererKeys = payload?.triggerType ? streamTriggerKeys(payload.triggerType) : [];
  const renderer = rendererKeys
    .map((key) => flowTriggerRenderers.get(key))
    .find((r) => r !== undefined);
  const RendererComponent = renderer?.component;
  const refOverrides = buildExternalRefOverrides(externalRefs, payload?.triggerData ?? null);

  return (
    <StreamCardBubble
      icon={<Play className="h-4 w-4" />}
      color="indigo"
      title={payload?.flowName ? `Automation started: ${payload.flowName}` : 'Automation started'}
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-started-bubble"
    >
      {RendererComponent ? (
        <div className="mt-2">
          <Suspense fallback={<p className="text-[11px] text-muted-foreground">Loading…</p>}>
            <RendererComponent
              triggerData={payload?.triggerData ?? null}
              surface="stream"
              externalRefs={externalRefs}
            />
          </Suspense>
        </div>
      ) : (
        payload?.triggerData &&
        Object.keys(payload.triggerData).length > 0 && (
          <div className="mt-2 space-y-1">
            <SectionLabel as="span" className="text-[10px]">
              Trigger data
            </SectionLabel>
            <div className="rounded border border-border/50 bg-muted/30 p-2">
              {Object.entries(payload.triggerData)
                .slice(0, 5)
                .map(([key, value]) => (
                  <div key={key} className="flex gap-2 text-xs">
                    <span className="shrink-0 font-medium text-muted-foreground">{key}:</span>
                    {refOverrides && Object.hasOwn(refOverrides, key) ? (
                      refOverrides[key]
                    ) : (
                      <span className="truncate">
                        {typeof value === 'string' ? value : JSON.stringify(value)}
                      </span>
                    )}
                  </div>
                ))}
              {Object.keys(payload.triggerData).length > 5 && (
                <p className="text-[10px] text-muted-foreground mt-1">
                  +{Object.keys(payload.triggerData).length - 5} more fields
                </p>
              )}
            </div>
          </div>
        )
      )}
    </StreamCardBubble>
  );
}

export function FlowNodeStartedBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    nodeType?: string;
  }>(event.payload);

  const icon = payload?.nodeType ? nodeTypeIcon(payload.nodeType) : null;

  return (
    <DividerBubble event={event}>
      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
        {icon}
        <span className="font-medium">{payload?.nodeName ?? 'Node'}</span>
        <span>started</span>
      </div>
    </DividerBubble>
  );
}

export function FlowNodeCompletedBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    nodeType?: string;
    outputLabel?: string;
    output?: Record<string, unknown>;
  }>(event.payload);

  const icon = payload?.nodeType ? nodeTypeIcon(payload.nodeType) : null;

  return (
    <DividerBubble event={event} className="text-status-success/80">
      <div className="flex items-center gap-1.5 text-xs">
        {icon ?? <CheckCircle2 className="h-3.5 w-3.5" />}
        <span className="font-medium">{payload?.nodeName ?? 'Node'}</span>
        <span className="text-muted-foreground">completed</span>
        {payload?.outputLabel && (
          <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono">
            {payload.outputLabel}
          </span>
        )}
      </div>
    </DividerBubble>
  );
}

export function FlowNodeErrorBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    error?: string;
  }>(event.payload);

  return (
    <StreamCardBubble
      icon={<AlertCircle className="h-4 w-4" />}
      color="red"
      title={`${payload?.nodeName ?? 'Node'} failed`}
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-node-error-bubble"
    >
      {payload?.error && <p className="mt-1 text-xs text-muted-foreground">{payload.error}</p>}
    </StreamCardBubble>
  );
}

export function FlowNodeRetryingBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    attempt?: number;
    maxAttempts?: number;
    error?: string;
  }>(event.payload);

  const attempts =
    payload?.attempt != null && payload?.maxAttempts != null
      ? ` (attempt ${payload.attempt} of ${payload.maxAttempts})`
      : '';

  return (
    <StreamCardBubble
      icon={<RefreshCw className="h-4 w-4" />}
      color="amber"
      title={`${payload?.nodeName ?? 'Node'} retrying after a transient provider error${attempts}`}
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-node-retrying-bubble"
    >
      {payload?.error && <p className="mt-1 text-xs text-muted-foreground">{payload.error}</p>}
    </StreamCardBubble>
  );
}

export function FlowApprovalRequestedBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    approvalType?: string;
  }>(event.payload);

  return (
    <StreamCardBubble
      icon={<Clock className="h-4 w-4" />}
      color="amber"
      title={`Waiting for approval: ${payload?.nodeName ?? 'Unknown'}`}
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-approval-requested-bubble"
    >
      {payload?.approvalType && (
        <p className="mt-1 text-xs text-muted-foreground">
          Type: {payload.approvalType.replace(/_/g, ' ')}
        </p>
      )}
    </StreamCardBubble>
  );
}

export function FlowApprovalRespondedBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    nodeName?: string;
    approved?: boolean;
    feedback?: string;
  }>(event.payload);

  const approved = payload?.approved ?? false;

  return (
    <StreamCardBubble
      icon={approved ? <CheckCircle2 className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
      color={approved ? 'teal' : 'red'}
      title={`${payload?.nodeName ?? 'Approval'}: ${approved ? 'Approved' : 'Rejected'}`}
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-approval-responded-bubble"
    >
      {payload?.feedback && (
        <p className="mt-1 whitespace-pre-wrap text-xs text-muted-foreground">{payload.feedback}</p>
      )}
    </StreamCardBubble>
  );
}

export function FlowCompletedBubble({ event }: FlowEventProps) {
  return (
    <StreamCardBubble
      icon={<CheckCircle2 className="h-4 w-4" />}
      color="teal"
      title="Automation completed"
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-completed-bubble"
    />
  );
}

export function FlowErrorBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{ error?: string }>(event.payload);

  return (
    <StreamCardBubble
      icon={<AlertCircle className="h-4 w-4" />}
      color="red"
      title="Automation failed"
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-error-bubble"
    >
      {payload?.error && <p className="mt-1 text-xs text-muted-foreground">{payload.error}</p>}
    </StreamCardBubble>
  );
}

export function FlowCancelledBubble({ event }: FlowEventProps) {
  return (
    <StreamCardBubble
      icon={<XCircle className="h-4 w-4" />}
      color="amber"
      title="Automation cancelled"
      timestamp={event.timestamp}
      event={event}
      data-testid="flow-cancelled-bubble"
    />
  );
}

interface FlowDeepLinkProps {
  flowRunId: string;
  nodeId: string;
  color: 'indigo' | 'teal';
  label: string;
}

function FlowRunDeepLink({ flowRunId, nodeId, color, label }: FlowDeepLinkProps) {
  const slug = useActiveOrgSlug();
  const colorClasses =
    color === 'teal'
      ? 'border-status-accent-teal/30 bg-status-accent-teal/10 text-status-accent-teal hover:bg-status-accent-teal/20'
      : 'border-status-accent-indigo/30 bg-status-accent-indigo/10 text-status-accent-indigo hover:bg-status-accent-indigo/20';
  return (
    <Link
      to={ROUTES.org(slug).flowRunNode(flowRunId, nodeId)}
      className={cn(
        'mt-2 inline-flex items-center gap-1.5 rounded-md border px-3 py-1.5 text-xs font-medium transition-colors',
        colorClasses,
      )}
    >
      {label}
      <ArrowRight className="h-3 w-3" />
    </Link>
  );
}

export function AgentSourceFlowBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    flowName?: string;
    flowRunId?: string;
    nodeId?: string;
    nodeName?: string;
    nodeType?: 'agent' | 'agent_task';
  }>(event.payload);

  const icon = payload?.nodeType ? nodeTypeIcon(payload.nodeType) : null;
  const flowName = payload?.flowName ?? 'flow';

  return (
    <StreamCardBubble
      icon={<Workflow className="h-4 w-4" />}
      color="indigo"
      title={`Started from automation: ${flowName}`}
      timestamp={event.timestamp}
      event={event}
      data-testid="agent-source-flow-bubble"
    >
      {payload?.nodeName && (
        <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
          {icon}
          <span className="font-medium text-foreground">{payload.nodeName}</span>
          {payload.nodeType && (
            <span className="rounded bg-muted/50 px-1.5 py-0.5 text-[10px] font-mono">
              {payload.nodeType}
            </span>
          )}
        </div>
      )}
      {payload?.flowRunId && payload.nodeId && (
        <FlowRunDeepLink
          flowRunId={payload.flowRunId}
          nodeId={payload.nodeId}
          color="indigo"
          label="Open automation run"
        />
      )}
    </StreamCardBubble>
  );
}

export function ReflexResultBubble({ event }: FlowEventProps) {
  const payload = parseEventPayload<{
    flowName?: string;
    flowRunId?: string;
    nodeId?: string;
    nodeName?: string;
    nodeType?: 'agent' | 'agent_task';
    summary?: string | null;
    structuredOutput?: Record<string, unknown> | null;
  }>(event.payload);

  const hasStructuredOutput =
    payload?.structuredOutput &&
    typeof payload.structuredOutput === 'object' &&
    Object.keys(payload.structuredOutput).length > 0;

  // Agents sometimes return their structured output as the summary string
  // itself (a raw JSON blob). Render it as a structured payload instead of
  // dumping the JSON through markdown; when the parsed summary duplicates
  // `structuredOutput`, drop it entirely.
  const summaryJson = payload?.summary ? parseJsonPayload(payload.summary) : null;
  const summaryText = summaryJson ? null : payload?.summary;
  const structuredValue = hasStructuredOutput ? payload?.structuredOutput : summaryJson;

  return (
    <StreamCardBubble
      icon={<CheckCircle2 className="h-4 w-4" />}
      color="teal"
      title="Returned to automation"
      timestamp={event.timestamp}
      event={event}
      data-testid="reflex-result-bubble"
    >
      {payload?.nodeName && (
        <p className="mt-1 text-xs text-muted-foreground">
          Result returned to <span className="font-medium text-foreground">{payload.nodeName}</span>
          {payload.flowName && (
            <>
              {' '}
              in <span className="font-medium text-foreground">{payload.flowName}</span>
            </>
          )}
        </p>
      )}
      {summaryText && (
        <div className="mt-2">
          <SectionLabel as="span" className="text-[10px]">
            Summary
          </SectionLabel>
          <div className="mt-0.5">
            <MarkdownContent content={summaryText} size="xs" />
          </div>
        </div>
      )}
      {structuredValue && (
        <div className="mt-2">
          <SectionLabel as="span" className="text-[10px]">
            {hasStructuredOutput ? 'Structured output' : 'Result'}
          </SectionLabel>
          <StructuredPayloadView value={structuredValue} compact />
        </div>
      )}
      {payload?.flowRunId && payload.nodeId && (
        <FlowRunDeepLink
          flowRunId={payload.flowRunId}
          nodeId={payload.nodeId}
          color="teal"
          label="Open automation run"
        />
      )}
    </StreamCardBubble>
  );
}
